import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './survey-answers.module.css'
import { interpolateRgb } from 'd3-interpolate';
import ReactEcharts from "echarts-for-react";
import { max } from 'moment';

const selectedColor = "#69B75C";
const grayColorScale = interpolateRgb("#012634", "#96CBDF");

const CheckboxAnswers = (props) => {
    const { t, question, totalAnswers } = props;
    const { data, name, order } = question;

    const [selectedIndex, setSelectedIndex] = useState(0)
    const onClickItem = useCallback(
        (index) => {
            setSelectedIndex(index)
        },
        [],
    )

    const percentage = totalAnswers ? (data[selectedIndex].count / totalAnswers * 100).toFixed(2) : "0.00"

    return (
        <>
            <p className={styles['question-title']}>{order}. {name}</p>
            {
                !totalAnswers
                    ? <p>N/D</p>
                    : <div className={styles['pie-and-text']}>
                        <div className={styles['pie']}>
                            <PolarBarChart data={data} onClickItem={onClickItem} />
                        </div>
                        <div className={styles['multiple-choice-answer']}>
                            <h6><span>{percentage}%</span> {t("ofTheAnswers")} ({data[selectedIndex].count})</h6>
                            <p><strong>{t("answer")}</strong>: {data[selectedIndex].name}</p>
                        </div>
                    </div>
            }
        </>
    )
}

const PolarBarChart = memo((props) => {
    const { data, onClickItem } = props;
    const chartRef = useRef(null);

    const getColor = (index) => {
        return grayColorScale(index / (3))
    }

    const option = useMemo(() => {
        if (!data || !data.length) return;

        let graphData = []
        let categories = []
        let maxValue = 0;

        for (let index = 0; index < data.length; index++) {
            const element = data[index];
            categories.push(element.name)
            if (element.count > maxValue) maxValue = element.count
            graphData.push({
                name: element.name,
                value: element.count,
                itemStyle: {
                    color: getColor(index)
                }
            })
        }

        const auxData = Array(data.length).fill(maxValue)

        const option = {
            polar: {
                radius: [0, '80%']
            },
            radiusAxis: {
                max: 4,
                axisLabel: { show: false },
                axisTick: { show: false }
            },
            angleAxis: {
                type: 'category',
                data: categories,
                axisLabel: { show: false },
                axisTick: { show: false }
            },
            series: [
                {
                    name: 'Total answers',
                    type: 'bar',
                    coordinateSystem: 'polar',
                    barCategoryGap: '0%',
                    barGap: '-100%',
                    silent: true,
                    emphasis: {
                        itemStyle: {
                            color: selectedColor
                        }
                    },
                    data: graphData
                },
                {
                    // TRANSPARENT DATA TO HANDLE HOVER OVER ITEMS WITH VALUE ZERO
                    name: 'Aux data',
                    type: 'bar',
                    coordinateSystem: 'polar',
                    barCategoryGap: '0%',
                    barGap: '-100%',
                    itemStyle: {
                        color: 'transparent',
                    },
                    data: auxData
                }
            ]
        };

        return option;
    }, [data])

    useEffect(() => {
        const chartInstance = chartRef.current.getEchartsInstance();
        if (chartInstance && data.length && option) {
            setTimeout(() => {
                chartInstance.dispatchAction({
                    type: 'highlight',
                    seriesIndex: 0,
                    dataIndex: 0,
                });
            }, 1000);
        }
    }, [option, data]);

    const onChartMouseOver = (params) => {
        const chartInstance = chartRef.current.getEchartsInstance();

        // Reset previous highlights
        chartInstance.dispatchAction({
            type: 'downplay',
            seriesIndex: 0,
        });
        chartInstance.dispatchAction({
            type: 'highlight',
            seriesIndex: 0,
            dataIndex: params.dataIndex,
        });

        onClickItem(params.dataIndex)
    }

    const onEvents = {
        'mouseover': onChartMouseOver
    }

    return <ReactEcharts
        ref={chartRef}
        option={option}
        onEvents={onEvents}
        style={{ height: 140 }}
        notMerge={true}
        lazyUpdate={true}
    />
})

export default CheckboxAnswers